import { useState, useCallback, ReactNode } from 'react'
import { TFunction } from 'i18next'
import { ConfirmDialog } from './ConfirmDialog'

interface DialogOptions {
  onConfirm?: () => void | Promise<void>
  onCancel?: () => void
  confirmText?: string
  cancelText?: string
  translate?: TFunction
}

interface DialogInstance {
  confirm: (message: string, options?: DialogOptions) => Promise<boolean>
  alert: (
    message: string,
    options?: Omit<DialogOptions, 'onCancel' | 'cancelText'>,
  ) => Promise<void>
  render: () => ReactNode
}

export const useDialog = (translate: TFunction): DialogInstance => {
  const [isOpen, setIsOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [options, setOptions] = useState<DialogOptions>({})

  const openDialog = useCallback((msg: string, opts: DialogOptions = {}) => {
    setMessage(msg)
    setOptions(opts)
    setIsOpen(true)
  }, [])

  const closeDialog = useCallback(() => {
    setIsOpen(false)
    setMessage('')
    setOptions({})
  }, [])

  const handleConfirm = useCallback(async () => {
    try {
      await options.onConfirm?.()
    } finally {
      closeDialog()
    }
  }, [options, closeDialog])

  const handleCancel = useCallback(() => {
    options.onCancel?.()
    closeDialog()
  }, [options, closeDialog])

  const confirm = useCallback(
    (msg: string, opts: DialogOptions = {}) => {
      return new Promise<boolean>((resolve) => {
        openDialog(msg, {
          ...opts,
          onConfirm: async () => {
            await opts.onConfirm?.()
            resolve(true)
          },
          onCancel: () => {
            opts.onCancel?.()
            resolve(false)
          },
        })
      })
    },
    [openDialog],
  )

  const alert = useCallback(
    (
      msg: string,
      opts: Omit<DialogOptions, 'onCancel' | 'cancelText'> = {},
    ) => {
      return new Promise<void>((resolve) => {
        openDialog(msg, {
          ...opts,
          onConfirm: async () => {
            await opts.onConfirm?.()
            resolve()
          },
          cancelText: undefined,
        })
      })
    },
    [openDialog],
  )

  const render = useCallback(
    () => (
      <ConfirmDialog
        isOpen={isOpen}
        message={message}
        onConfirm={handleConfirm}
        onClose={handleCancel}
        translate={translate}
        confirmText={options.confirmText}
        cancelText={options.cancelText}
      />
    ),
    [
      isOpen,
      message,
      handleConfirm,
      handleCancel,
      translate,
      options.confirmText,
      options.cancelText,
    ],
  )

  return {
    confirm,
    alert,
    render,
  }
}

export default useDialog
